import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Icon from '../AppIcon';
import { TIMEZONE_OPTIONS } from '../../utils/timezoneUtils';

const INDUSTRY_OPTIONS = [
  'Technology', 'Healthcare', 'Finance', 'Education', 'Marketing',
  'E-commerce', 'Real Estate', 'Consulting', 'Manufacturing', 
  'Media & Entertainment', 'Non-profit', 'Government', 'Other'
];

const COMPANY_SIZE_OPTIONS = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '1000+', label: '1000+ employees' }
];

const CULTURE_VALUES = [
  'Remote-first', 'Work-life balance', 'Innovation', 'Collaboration',
  'Diversity & Inclusion', 'Continuous learning', 'Fast-paced',
  'Customer-focused', 'Data-driven', 'Entrepreneurial', 'Flexible',
  'Results-oriented', 'Team-oriented', 'Growth-minded'
];

const BENEFITS_OPTIONS = [
  'Health insurance', 'Dental insurance', 'Vision insurance',
  'Flexible hours', 'Remote work', 'Professional development',
  'Paid time off', 'Retirement plan', 'Equipment provided',
  'Wellness programs', 'Team events', 'Performance bonuses',
  'Gym membership', 'Mental health support', 'Learning stipend'
];

const COMMUNICATION_PREFERENCES = [
  'Slack', 'Microsoft Teams', 'Email', 'Video calls', 'Asana',
  'Trello', 'Monday.com', 'Discord', 'WhatsApp', 'Zoom',
  'Google Meet', 'Notion', 'ClickUp'
];

const BUDGET_RANGES = [
  '$5-15/hour', '$15-25/hour', '$25-40/hour', '$40-60/hour', 
  '$60+/hour', 'Project-based', 'Monthly retainer'
];

const EnhancedCompanyProfileForm = ({ onSuccess }) => {
  const { userProfile, updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    company_name: userProfile?.company_name || '',
    bio: userProfile?.bio || '',
    website: userProfile?.website || '',
    linkedin: userProfile?.linkedin || '',
    location: userProfile?.location || '',
    phone: userProfile?.phone || '',
    company_size: userProfile?.company_size || '',
    industry: userProfile?.industry || '',
    founded_year: userProfile?.founded_year || '',
    company_culture: Array.isArray(userProfile?.company_culture) ? userProfile.company_culture : (userProfile?.company_culture ? [userProfile.company_culture] : []),
    benefits: Array.isArray(userProfile?.benefits) ? userProfile.benefits : (userProfile?.benefits ? [userProfile.benefits] : []),
    timezone: userProfile?.timezone || '',
    preferred_communication: Array.isArray(userProfile?.preferred_communication) ? userProfile.preferred_communication : [],
    hiring_budget_range: userProfile?.hiring_budget_range || '',
    certifications: Array.isArray(userProfile?.certifications) ? userProfile.certifications : [],
    languages_spoken: Array.isArray(userProfile?.languages_spoken) ? userProfile.languages_spoken : ['English'],
    company_logo: userProfile?.company_logo || '',
    company_banner: userProfile?.company_banner || '',
  });

  useEffect(() => {
    if (userProfile) {
      setFormData({
        company_name: userProfile.company_name || '',
        bio: userProfile.bio || '',
        website: userProfile.website || '',
        linkedin: userProfile.linkedin || '',
        location: userProfile.location || '',
        phone: userProfile.phone || '',
        company_size: userProfile.company_size || '',
        industry: userProfile.industry || '',
        founded_year: userProfile.founded_year || '',
        company_culture: Array.isArray(userProfile.company_culture) ? userProfile.company_culture : (userProfile.company_culture ? [userProfile.company_culture] : []),
        benefits: Array.isArray(userProfile.benefits) ? userProfile.benefits : (userProfile.benefits ? [userProfile.benefits] : []),
        timezone: userProfile.timezone || '',
        preferred_communication: Array.isArray(userProfile.preferred_communication) ? userProfile.preferred_communication : [],
        hiring_budget_range: userProfile.hiring_budget_range || '',
        certifications: Array.isArray(userProfile.certifications) ? userProfile.certifications : [],
        languages_spoken: Array.isArray(userProfile.languages_spoken) ? userProfile.languages_spoken : ['English'],
        company_logo: userProfile.company_logo || '',
        company_banner: userProfile.company_banner || '',
      });
    }
  }, [userProfile]);

  const handleArrayFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Ensure all array fields are properly formatted for database
      const dataToSubmit = {
        ...formData,
        // Ensure array fields are arrays
        company_culture: Array.isArray(formData.company_culture) ? formData.company_culture : [],
        benefits: Array.isArray(formData.benefits) ? formData.benefits : [],
        preferred_communication: Array.isArray(formData.preferred_communication) ? formData.preferred_communication : [],
        certifications: Array.isArray(formData.certifications) ? formData.certifications : [],
        languages_spoken: Array.isArray(formData.languages_spoken) ? formData.languages_spoken : ['English'],
      };

      const result = await updateProfile(dataToSubmit);
      if (result.success) {
        if (onSuccess) {
          onSuccess(result.data);
        } else {
          alert('Company profile updated successfully!');
        }
      } else {
        alert('Error updating profile: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: 'Building2' },
    { id: 'culture', label: 'Culture & Values', icon: 'Heart' },
    { id: 'benefits', label: 'Benefits & Budget', icon: 'Gift' },
    { id: 'communication', label: 'Work Style', icon: 'MessageCircle' },
    { id: 'branding', label: 'Branding', icon: 'Image' }
  ];

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Company Name *
          </label>
          <input
            type="text"
            value={formData.company_name}
            onChange={(e) => handleInputChange('company_name', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Industry
          </label>
          <select
            value={formData.industry}
            onChange={(e) => handleInputChange('industry', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Select Industry</option>
            {INDUSTRY_OPTIONS.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Company Size
          </label>
          <select
            value={formData.company_size}
            onChange={(e) => handleInputChange('company_size', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Select Size</option>
            {COMPANY_SIZE_OPTIONS.map(size => (
              <option key={size.value} value={size.value}>{size.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Founded Year
          </label>
          <input
            type="number"
            min="1800"
            max={new Date().getFullYear()}
            value={formData.founded_year}
            onChange={(e) => handleInputChange('founded_year', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Location
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Website
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            LinkedIn
          </label>
          <input
            type="url"
            value={formData.linkedin}
            onChange={(e) => handleInputChange('linkedin', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Company Description
        </label>
        <textarea
          value={formData.bio}
          onChange={(e) => handleInputChange('bio', e.target.value)}
          rows={4}
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Tell VAs about your company, mission, and what makes you a great place to work..."
        />
      </div>
    </div>
  );

  const renderCultureValues = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-text-primary mb-4">Company Culture & Values</h4>
        <p className="text-sm text-text-secondary mb-4">
          Select the values that best represent your company culture. This helps VAs understand your work environment.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {CULTURE_VALUES.map(value => (
            <label key={value} className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-secondary-50 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.company_culture.includes(value)}
                onChange={() => handleArrayFieldChange('company_culture', value)}
                className="rounded border-border focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-text-primary">{value}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBenefitsAndBudget = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Typical Hiring Budget Range
        </label>
        <select
          value={formData.hiring_budget_range}
          onChange={(e) => handleInputChange('hiring_budget_range', e.target.value)}
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="">Select Budget Range</option>
          {BUDGET_RANGES.map(range => (
            <option key={range} value={range}>{range}</option>
          ))}
        </select>
      </div>

      <div>
        <h4 className="text-lg font-medium text-text-primary mb-4">Benefits Offered</h4>
        <p className="text-sm text-text-secondary mb-4">
          Select all benefits you typically offer to VAs. This helps attract the right candidates.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {BENEFITS_OPTIONS.map(benefit => (
            <label key={benefit} className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-secondary-50 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.benefits.includes(benefit)}
                onChange={() => handleArrayFieldChange('benefits', benefit)}
                className="rounded border-border focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-text-primary">{benefit}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderWorkStyle = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Primary Time Zone
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => handleInputChange('timezone', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Select Time Zone</option>
            {TIMEZONE_OPTIONS.map(tz => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-text-tertiary mt-1">
            Used for interview scheduling and communication
          </p>
        </div>
      </div>

      <div>
        <h4 className="text-lg font-medium text-text-primary mb-4">Preferred Communication Tools</h4>
        <p className="text-sm text-text-secondary mb-4">
          Select the tools you prefer to use for communication and project management.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {COMMUNICATION_PREFERENCES.map(tool => (
            <label key={tool} className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-secondary-50 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.preferred_communication.includes(tool)}
                onChange={() => handleArrayFieldChange('preferred_communication', tool)}
                className="rounded border-border focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-text-primary">{tool}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-lg font-medium text-text-primary mb-4">Languages Spoken</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {['English', 'Spanish', 'French', 'German', 'Portuguese', 'Mandarin', 'Japanese', 'Other'].map(language => (
            <label key={language} className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-secondary-50 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.languages_spoken.includes(language)}
                onChange={() => handleArrayFieldChange('languages_spoken', language)}
                className="rounded border-border focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-text-primary">{language}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBranding = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Company Logo URL
        </label>
        <input
          type="url"
          value={formData.company_logo}
          onChange={(e) => handleInputChange('company_logo', e.target.value)}
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="https://example.com/logo.png"
        />
        <p className="text-xs text-text-muted mt-1">
          Recommended: Square image, 200x200px minimum
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Company Banner URL
        </label>
        <input
          type="url"
          value={formData.company_banner}
          onChange={(e) => handleInputChange('company_banner', e.target.value)}
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="https://example.com/banner.png"
        />
        <p className="text-xs text-text-muted mt-1">
          Recommended: 1200x400px, showcases your company culture
        </p>
      </div>

      <div>
        <h4 className="text-lg font-medium text-text-primary mb-4">Certifications & Credentials</h4>
        <textarea
          value={Array.isArray(formData.certifications) ? formData.certifications.join('\n') : ''}
          onChange={(e) => handleInputChange('certifications', e.target.value.split('\n').filter(cert => cert.trim()))}
          rows={4}
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Enter each certification on a new line&#10;ISO 9001:2015&#10;SOC 2 Compliance&#10;Google Partner"
        />
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic': return renderBasicInfo();
      case 'culture': return renderCultureValues();
      case 'benefits': return renderBenefitsAndBudget();
      case 'communication': return renderWorkStyle();
      case 'branding': return renderBranding();
      default: return renderBasicInfo();
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-surface p-6">
        <h2 className="text-2xl font-bold text-text-primary mb-6">Enhanced Company Profile</h2>
        
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary text-white border-b-2 border-primary'
                  : 'text-text-secondary hover:text-text-primary hover:bg-secondary-50'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {renderTabContent()}

          <div className="flex justify-end pt-6 mt-6 border-t border-border">
            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading && <Icon name="Loader2" size={16} className="animate-spin" />}
              <span>{loading ? 'Updating...' : 'Update Company Profile'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnhancedCompanyProfileForm;
